// ⬡B:aba_shared.ccwa_core.components.luma_widget:CODE:luma_widget_inline_welcome_state:20260611⬡
// LumaWidget — every-turn explanation widget for the WidgetRail.
// Props:
//   entries  — array of { turnId, at, layman, technical }
//              sorted newest-first by the caller, or sorted by `at` here.
// Empty state: a living welcome card (inline styles, no CSS dependency)
// telling Brandon that Luma will write her turn explanation here.

import React, { useState } from 'react';

// ── shared inline style tokens ────────────────────────────────────────────────
const font = 'system-ui, -apple-system, sans-serif';

const welcomeCardStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '16px',
  borderRadius: '12px',
  background: 'rgba(16, 185, 129, 0.08)',
  border: '1px solid rgba(16,185,129,0.22)',
  fontFamily: font,
};

const welcomeAvatarRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const avatarStyle = {
  width: '34px',
  height: '34px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #10b981 0%, #065f46 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 700,
  color: '#fff',
  flexShrink: 0,
  fontFamily: font,
};

const welcomeNameStyle = {
  fontSize: '13px',
  fontWeight: 700,
  color: 'rgba(16,185,129,0.95)',
  fontFamily: font,
};

const welcomeSubStyle = {
  fontSize: '11px',
  color: 'rgba(255,255,255,0.40)',
  fontFamily: font,
};

const welcomeBodyStyle = {
  fontSize: '13px',
  lineHeight: '1.55',
  color: 'rgba(255,255,255,0.72)',
  fontFamily: font,
};

const welcomeHintStyle = {
  fontSize: '11px',
  color: 'rgba(255,255,255,0.32)',
  fontFamily: font,
  borderTop: '1px solid rgba(255,255,255,0.07)',
  paddingTop: '8px',
};

// ── card for a real turn entry ────────────────────────────────────────────────
function EntryCard({ entry, isHero }) {
  const [flipped, setFlipped] = useState(false);

  const primary   = flipped ? entry.technical : entry.layman;
  const secondary = flipped ? entry.layman    : entry.technical;
  const label     = flipped ? 'Technical'     : 'Layman';
  const nextLabel = flipped ? 'Layman'        : 'Technical';

  const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: isHero ? '14px' : '10px 12px',
    borderRadius: '11px',
    background: isHero ? 'rgba(16,185,129,0.10)' : 'rgba(255,255,255,0.04)',
    border: isHero ? '1px solid rgba(16,185,129,0.25)' : '1px solid rgba(255,255,255,0.07)',
    fontFamily: font,
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    color: 'rgba(255,255,255,0.40)',
    fontFamily: font,
  };

  const toggleStyle = {
    marginLeft: 'auto',
    background: 'rgba(16,185,129,0.18)',
    border: '1px solid rgba(16,185,129,0.30)',
    borderRadius: '5px',
    color: 'rgba(16,185,129,0.90)',
    fontSize: '10px',
    fontWeight: 700,
    padding: '2px 7px',
    cursor: 'pointer',
    fontFamily: font,
  };

  const primaryStyle = {
    fontSize: isHero ? '14px' : '13px',
    fontWeight: isHero ? 600 : 400,
    color: 'rgba(255,255,255,0.88)',
    lineHeight: '1.5',
    fontFamily: font,
  };

  const secondaryStyle = {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.38)',
    lineHeight: '1.4',
    fontFamily: font,
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span>Turn {entry.turnId}</span>
        <span>{new Date(entry.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        <button
          style={toggleStyle}
          onClick={() => setFlipped(f => !f)}
          title={`Switch to ${nextLabel}`}
          aria-label={`Currently showing ${label} — tap to switch`}
        >
          {label}
        </button>
      </div>
      <p style={{ ...primaryStyle, margin: 0 }}>{primary}</p>
      <p style={{ ...secondaryStyle, margin: 0 }}>{secondary}</p>
    </div>
  );
}

// ── main export ───────────────────────────────────────────────────────────────
export function LumaWidget({ entries = [] }) {
  const sorted = [...entries].sort((a, b) => new Date(b.at) - new Date(a.at));
  const [hero, ...past] = sorted;

  // Empty state: living welcome card, full inline styles.
  if (!hero) {
    return (
      <div style={welcomeCardStyle}>
        <div style={welcomeAvatarRowStyle}>
          <div style={avatarStyle}>LU</div>
          <div>
            <div style={welcomeNameStyle}>Luma</div>
            <div style={welcomeSubStyle}>Turn-by-turn explanation</div>
          </div>
        </div>
        <p style={{ ...welcomeBodyStyle, margin: 0 }}>
          Every time you send a message, I will write my explanation right here,
          in plain terms and technical detail. You will always know exactly what
          A’NU did and why.
        </p>
        <div style={welcomeHintStyle}>Waiting for your first message.</div>
      </div>
    );
  }

  const listStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const pastListStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '6px',
  };

  return (
    <div style={listStyle}>
      <EntryCard entry={hero} isHero />
      {past.length > 0 && (
        <div style={pastListStyle}>
          {past.map(entry => (
            <EntryCard key={entry.turnId} entry={entry} isHero={false} />
          ))}
        </div>
      )}
    </div>
  );
}

export default LumaWidget;
